export type PickedFile = {
  path: string
  filename: string
  mime: string
  size: number
  width?: number
  height?: number
}

type PickOptions = {
  multiple?: boolean
  camera?: boolean
  useFrontCamera?: boolean
  mediaType?: "photo" | "video" | "any"
  maxFiles?: number
}

function getImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = reject
    img.src = src
  })
}

async function fileToPicked(file: File): Promise<PickedFile> {
  const path = URL.createObjectURL(file)
  const picked: PickedFile = {
    path,
    filename: file.name || `file_${Date.now()}`,
    mime: file.type || "application/octet-stream",
    size: file.size,
  }
  if (file.type.startsWith("image/")) {
    try {
      const dims = await getImageDimensions(path)
      picked.width = dims.width
      picked.height = dims.height
    } catch {
      /* ignore */
    }
  }
  return picked
}

/** 浏览器 <input type="file"> 选图/拍照，返回与 RN ImagePicker 兼容的结构 */
export function pickFiles(options: PickOptions = {}): Promise<PickedFile | PickedFile[] | null> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(null)
      return
    }

    let settled = false
    const input = document.createElement("input")
    input.type = "file"
    input.style.display = "none"

    if (options.camera) {
      input.capture = options.useFrontCamera ? "user" : "environment"
      input.accept = "image/*"
    } else if (options.mediaType === "photo") {
      input.accept = "image/*"
    } else if (options.mediaType === "video") {
      input.accept = "video/*"
    } else {
      input.accept = "image/*,video/*"
    }

    if (options.multiple) input.multiple = true

    const finish = (value: PickedFile | PickedFile[] | null) => {
      if (settled) return
      settled = true
      input.remove()
      resolve(value)
    }

    input.addEventListener("change", async () => {
      const list = input.files ? Array.from(input.files) : []
      if (!list.length) {
        finish(null)
        return
      }
      const limited = options.maxFiles ? list.slice(0, options.maxFiles) : list
      const picked = await Promise.all(limited.map(fileToPicked))
      finish(options.multiple ? picked : picked[0] ?? null)
    })

    // 用户取消选择时 resolve null
    window.addEventListener(
      "focus",
      () => {
        window.setTimeout(() => {
          if (!settled && !input.files?.length) finish(null)
        }, 400)
      },
      { once: true },
    )

    document.body.appendChild(input)
    input.click()
  })
}
